import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Clock, BookOpen, CheckCircle2, Award, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const CourseDetail = ({ user, onLogout }) => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [course, setCourse] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [showQuiz, setShowQuiz] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [videoWatched, setVideoWatched] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [maxWatchedTime, setMaxWatchedTime] = useState(0);
  const [timeOnPage, setTimeOnPage] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const videoRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    loadCourse();
  }, [courseId]);

  // Timer for embedded videos (YouTube/Vimeo) - Only runs when video is playing
  useEffect(() => {
    if (course && course.videoUrl && !videoWatched && isVideoPlaying) {
      // Start timer only when video is playing
      timerRef.current = setInterval(() => {
        setTimeOnPage(prev => {
          const newTime = prev + 1;
          
          // Calculate required watch time (95% of video duration in seconds)
          const requiredTime = Math.floor((course.duration * 60) * 0.95);
          const progress = Math.min((newTime / requiredTime) * 100, 100);
          setVideoProgress(progress);
          
          // Mark as watched if enough time has passed
          if (newTime >= requiredTime && !videoWatched) {
            setVideoWatched(true);
            toast.success(t('courseDetail.viewingCompleted'));
            if (timerRef.current) {
              clearInterval(timerRef.current);
            }
          }
          
          return newTime;
        });
      }, 1000); // Update every second
    } else {
      // Stop timer when video is paused or not playing
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    
    // Cleanup
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [course, videoWatched, isVideoPlaying]);

  // Auto-fullscreen when device rotates to landscape while video is playing
  useEffect(() => {
    const handleOrientation = () => {
      const isLandscape = window.matchMedia('(orientation: landscape)').matches;
      const video = videoRef.current;
      if (!video) return;

      if (isLandscape && isVideoPlaying) {
        // Enter fullscreen
        if (video.requestFullscreen) video.requestFullscreen();
        else if (video.webkitEnterFullscreen) video.webkitEnterFullscreen();
        else if (video.webkitRequestFullscreen) video.webkitRequestFullscreen();
      }
    };

    const mql = window.matchMedia('(orientation: landscape)');
    mql.addEventListener('change', handleOrientation);
    return () => mql.removeEventListener('change', handleOrientation);
  }, [isVideoPlaying]);

  const loadCourse = async () => {
    try {
      const courseRes = await axiosInstance.get(`/courses/${courseId}`);
      setCourse(courseRes.data);
      
      try {
        const quizRes = await axiosInstance.get(`/quizzes/${courseId}`);
        setQuiz(quizRes.data);
        setAnswers(new Array(quizRes.data.questions.length).fill(-1));
      } catch (err) {
        // Quiz might not exist
      }
    } catch (error) {
      toast.error(t('courseDetail.failedToLoad'));
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionIndex, answerIndex) => {
    const newAnswers = [...answers];
    newAnswers[questionIndex] = answerIndex;
    setAnswers(newAnswers);
  };

  // Video tracking functions
  const handleVideoTimeUpdate = (e) => {
    const video = e.target;
    const currentTime = video.currentTime;
    const duration = video.duration;
    
    // Update max watched time (prevents rewinding)
    if (currentTime > maxWatchedTime) {
      setMaxWatchedTime(currentTime);
    }
    
    // Calculate progress
    const progress = (currentTime / duration) * 100;
    setVideoProgress(progress);
    
    // Mark as watched if 95% or more completed
    if (progress >= 95 && !videoWatched) {
      setVideoWatched(true);
      toast.success(t('courseDetail.videoCompleted'));
    }
  };

  const handleVideoSeeking = (e) => {
    const video = e.target;
    
    // Prevent seeking forward beyond max watched time
    if (video.currentTime > maxWatchedTime + 1) {
      video.currentTime = maxWatchedTime;
      toast.error(t('courseDetail.cannotSkip'));
    }
  };
  
  const handleVideoPlay = () => {
    setIsVideoPlaying(true);
  };
  
  const handleVideoPause = () => {
    setIsVideoPlaying(false);
  };

  const handleTakeQuiz = () => {
    if (course.videoUrl && !videoWatched) {
      toast.error(t('courseDetail.watchFullVideo'));
      return;
    }
    setShowQuiz(true);
  };

  const submitQuiz = async () => {
    if (answers.some(a => a === -1)) {
      toast.error(t('courseDetail.answerAll'));
      return;
    }

    setSubmitting(true);
    try {
      const response = await axiosInstance.post('/quizzes/submit', {
        courseId,
        answers
      });
      
      if (response.data.passed) {
        toast.success(t('courseDetail.congratsPassed', { score: response.data.score }));
      } else {
        toast.error(t('courseDetail.failedScore', { score: response.data.score }));
      }
      
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (error) {
      toast.error(t('courseDetail.failedToSubmit'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-gray-100">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <Button
          onClick={() => navigate('/dashboard')}
          variant="outline"
          className="mb-6"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('courseDetail.backToDashboard')}
        </Button>

        {!showQuiz ? (
          <div className="space-y-6">
            {/* Course Header */}
            <Card>
              <div className="h-48 bg-gradient-to-br from-orange-500 to-gray-900 flex items-center justify-center rounded-t-lg p-8">
                <h1 className="text-4xl font-bold text-white text-center leading-tight">
                  {course.title}
                </h1>
              </div>
              <CardHeader>
                <CardDescription className="text-base">{course.description}</CardDescription>
                <div className="flex items-center space-x-4 mt-4 text-sm text-slate-600">
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-1" />
                    {course.duration} {t('courseDetail.minutes')}
                  </div>
                  <div className="flex items-center">
                    <BookOpen className="w-4 h-4 mr-1" />
                    {t('courseDetail.safetyTraining')}
                  </div>
                  <Badge className="bg-orange-100 text-orange-800 text-sm px-3 py-1">
                    {course.category}
                  </Badge>
                </div>
              </CardHeader>
            </Card>

            {/* Training Video */}
            {course.videoUrl ? (
              <Card>
                <CardHeader>
                  <CardTitle>{t('courseDetail.trainingVideo')}</CardTitle>
                  <CardDescription>{t('courseDetail.watchVideoDesc')}</CardDescription>
                </CardHeader>
                <CardContent>
                  {course.videoUrl.endsWith('.mp4') || course.videoUrl.includes('customer-assets') || course.videoUrl.includes('/stream/video/') || course.videoUrl.includes('/uploads/videos/') ? (
                    // Direct video file (MP4)
                    <div className="space-y-4">
                      <video
                        ref={videoRef}
                        className="w-full rounded-lg"
                        controls
                        controlsList="nodownload noremoteplayback"
                        playsInline
                        webkit-playsinline="true"
                        preload="metadata"
                        onTimeUpdate={handleVideoTimeUpdate}
                        onSeeking={handleVideoSeeking}
                        onPlay={handleVideoPlay}
                        onPause={handleVideoPause}
                        onContextMenu={(e) => e.preventDefault()}
                        style={{ maxHeight: '70vh' }}
                      >
                        <source src={course.videoUrl.startsWith('/api/') ? `${axiosInstance.defaults.baseURL.replace('/api', '')}${course.videoUrl}` : course.videoUrl} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                      
                      {/* Video Progress Bar */}
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">
                            {t('courseDetail.videoProgress')}
                            {!videoWatched && (
                              <span className={`ml-2 ${isVideoPlaying ? 'text-green-600' : 'text-orange-600'}`}>
                                {isVideoPlaying ? `▶ ${t('courseDetail.playing')}` : `⏸ ${t('courseDetail.paused')}`}
                              </span>
                            )}
                          </span>
                          <span className={`font-semibold ${videoWatched ? 'text-green-600' : 'text-gray-600'}`}>
                            {videoProgress.toFixed(0)}% {videoWatched && `✓ ${t('courseDetail.completed')}`}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div 
                            className={`h-2.5 rounded-full transition-all ${videoWatched ? 'bg-green-600' : 'bg-orange-500'}`}
                            style={{ width: `${Math.min(videoProgress, 100)}%` }}
                          ></div>
                        </div>
                        {!videoWatched && (
                          <p className="text-xs text-orange-600">
                            ⚠️ {t('courseDetail.mustWatch95')}
                          </p>
                        )}
                      </div>
                    </div>
                  ) : (
                    // Embedded video (YouTube/Vimeo)
                    <div className="space-y-4">
                      <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                        <iframe
                          className="absolute top-0 left-0 w-full h-full rounded-lg"
                          src={course.videoUrl}
                          title={course.title}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      </div>
                      
                      {/* Video Progress Bar for Embedded Videos */}
                      <div className="space-y-2">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-600">
                            {t('courseDetail.viewingProgress')}
                            {!videoWatched && (
                              <span className={`ml-2 ${isVideoPlaying ? 'text-green-600' : 'text-orange-600'}`}>
                                {isVideoPlaying ? `▶ ${t('courseDetail.timerRunning')}` : `⏸ ${t('courseDetail.timerPaused')}`}
                              </span>
                            )}
                          </span>
                          <span className={`font-semibold ${videoWatched ? 'text-green-600' : 'text-gray-600'}`}>
                            {videoProgress.toFixed(0)}% {videoWatched && `✓ ${t('courseDetail.completed')}`}
                          </span>
                        </div>
                        {!videoWatched && (
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              size="sm"
                              variant={isVideoPlaying ? "destructive" : "default"}
                              onClick={() => setIsVideoPlaying(!isVideoPlaying)}
                            >
                              {isVideoPlaying ? `⏸ ${t('courseDetail.pauseTimer')}` : `▶ ${t('courseDetail.resumeTimer')}`}
                            </Button>
                            <span className="text-xs text-slate-500">
                              {isVideoPlaying ? t('courseDetail.timerIsRunning') : t('courseDetail.timerIsPaused')}
                            </span>
                          </div>
                        )}
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div 
                            className={`h-2.5 rounded-full transition-all ${videoWatched ? 'bg-green-600' : 'bg-orange-500'}`}
                            style={{ width: `${Math.min(videoProgress, 100)}%` }}
                          ></div>
                        </div>
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                          <p className="text-xs text-gray-800">
                            <strong>⏱️ {t('courseDetail.timeTracking')}:</strong> {t('courseDetail.timeTrackingDesc', { seconds: Math.floor((course.duration * 60) * 0.95), minutes: Math.floor(course.duration * 0.95), remaining: Math.max(0, Math.floor((course.duration * 60 * 0.95) - timeOnPage)) })}
                          </p>
                        </div>
                        {!videoWatched && (
                          <p className="text-xs text-orange-600">
                            ⚠️ {t('courseDetail.keepTimerRunning')}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : user?.role && ['company_admin', 'division_admin', 'super_admin'].includes(user.role) ? (
              <Card className="bg-gradient-to-r from-orange-50 to-gray-100 border-orange-200">
                <CardHeader>
                  <CardTitle className="flex items-center text-gray-800">
                    <BookOpen className="w-5 h-5 mr-2" />
                    Training Resources
                  </CardTitle>
                  <CardDescription className="text-gray-700">
                    {t('courseDetail.trainingResourcesDesc')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="text-sm text-gray-700">
                  <ul className="list-disc list-inside space-y-2">
                    <li>Upload videos to <strong>Vimeo</strong> or <strong>YouTube</strong> (ensure embedding is enabled)</li>
                    <li>Use format: <code className="bg-orange-100 px-2 py-1 rounded">https://player.vimeo.com/video/VIDEO_ID</code></li>
                    <li>Or: <code className="bg-orange-100 px-2 py-1 rounded">https://www.youtube.com/embed/VIDEO_ID</code></li>
                    <li>Add the URL via Admin Dashboard when creating/editing courses</li>
                  </ul>
                </CardContent>
              </Card>
            ) : null}

            {/* Course Content */}
            <Card>
              <CardHeader>
                <CardTitle>{t('courseDetail.courseContent')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose prose-slate max-w-none">
                  <div className="whitespace-pre-wrap text-slate-700 leading-relaxed">
                    {course.content}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Take Quiz Button */}
            {quiz && (
              <Card className={`${
                course.videoUrl && !videoWatched 
                  ? 'bg-gray-300 text-gray-600 border-gray-400' 
                  : 'bg-gradient-to-r from-orange-500 to-gray-900 text-white'
              }`}>
                <CardHeader>
                  <CardTitle className={`flex items-center ${course.videoUrl && !videoWatched ? 'text-gray-600' : 'text-white'}`}>
                    {course.videoUrl && !videoWatched ? (
                      <Lock className="w-6 h-6 mr-2" />
                    ) : (
                      <Award className="w-6 h-6 mr-2" />
                    )}
                    {course.videoUrl && !videoWatched ? t('courseDetail.quizLocked') : t('courseDetail.readyForQuiz')}
                  </CardTitle>
                  <CardDescription className={course.videoUrl && !videoWatched ? 'text-gray-500' : 'text-orange-50'}>
                    {course.videoUrl && !videoWatched 
                      ? t('courseDetail.completeVideoToUnlock')
                      : t('courseDetail.testKnowledge')
                    }
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    onClick={handleTakeQuiz}
                    data-testid="take-quiz-button"
                    disabled={course.videoUrl && !videoWatched}
                    className={`font-semibold ${
                      course.videoUrl && !videoWatched
                        ? 'bg-gray-400 text-gray-600 cursor-not-allowed hover:bg-gray-400'
                        : 'bg-white text-orange-600 hover:bg-orange-50'
                    }`}
                  >
                    {course.videoUrl && !videoWatched ? (
                      <>
                        <Lock className="w-4 h-4 mr-2" />
                        {t('courseDetail.watchVideoFirst')}
                      </>
                    ) : (
                      t('courseDetail.takeQuiz')
                    )}
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">{t('courseDetail.courseQuiz')}</CardTitle>
                <CardDescription>
                  {t('courseDetail.quizDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                {quiz.questions.map((question, qIndex) => (
                  <div key={qIndex} className="space-y-4" data-testid={`question-${qIndex}`}>
                    <h3 className="font-semibold text-lg text-slate-800">
                      {qIndex + 1}. {question.question}
                    </h3>
                    <RadioGroup
                      value={answers[qIndex].toString()}
                      onValueChange={(value) => handleAnswerChange(qIndex, parseInt(value))}
                    >
                      {question.options.map((option, oIndex) => (
                        <div key={oIndex} className="flex items-center space-x-2 p-3 rounded-lg hover:bg-slate-50">
                          <RadioGroupItem
                            value={oIndex.toString()}
                            id={`q${qIndex}-o${oIndex}`}
                            data-testid={`question-${qIndex}-option-${oIndex}`}
                          />
                          <Label
                            htmlFor={`q${qIndex}-o${oIndex}`}
                            className="flex-1 cursor-pointer text-slate-700"
                          >
                            {option}
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                ))}
              </CardContent>
            </Card>

            <div className="flex space-x-4">
              <Button
                onClick={() => setShowQuiz(false)}
                variant="outline"
                data-testid="back-to-course"
              >
                {t('courseDetail.backToCourse')}
              </Button>
              <Button
                onClick={submitQuiz}
                disabled={submitting}
                data-testid="submit-quiz-button"
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
              >
                {submitting ? t('courseDetail.submittingQuiz') : t('courseDetail.submitQuiz')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseDetail;
